(() => {
  if (customElements.get('rwm-drawer-gifts')) return;
  const state = { queue: Promise.resolve(), draft: null, revision: 0, saved: 0, checkout: false };
  class RwmDrawerGifts extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.field = this.querySelector('[data-message]');
      if (state.revision > state.saved && this.cardSelected()) this.field.value = state.draft || '';
      else { state.draft = this.field.value; state.saved = state.revision; }
      this.sync();
      this.addEventListener('input', event => {
        if (event.target !== this.field) return;
        state.draft = this.field.value;
        state.revision++;
        this.sync();
        this.status(this.dataset.saving);
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.enqueue('text'), 650);
      }, opts);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-option]')) return;
        if (!this.cardSelected()) { this.field.value = ''; state.draft = ''; state.revision++; }
        this.sync();
        this.enqueue('options');
      }, opts);
      this.querySelector('[data-retry]').addEventListener('click', () => this.enqueue('options'), opts);
      if (state.revision > state.saved) this.timer = setTimeout(() => this.enqueue('text'), 650);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); }
    cardSelected() { return Boolean(this.querySelector('[data-option="card"]')?.checked); }
    sync() {
      this.querySelector('.rwm-drawer-gifts__message').hidden = !this.cardSelected();
      this.querySelector('[data-count]').textContent = this.field.value.length + '/100';
    }
    status(message, error = false) {
      const node = this.querySelector('[role="status"]');
      node.textContent = message;
      node.dataset.error = String(error);
      this.querySelector('[data-retry]').hidden = !error;
    }
    snapshot() {
      const options = [...this.querySelectorAll('[data-option]')].map(input => ({ id: Number(input.dataset.variant), kind: input.dataset.option, selected: input.checked }));
      const card = options.some(option => option.kind === 'card' && option.selected);
      const attributes = { 'gift-note': card ? this.field.value.trim() : '' };
      if (!card) { attributes['gift-from'] = ''; attributes['gift-to'] = ''; }
      return { options, attributes, revision: state.revision };
    }
    async request(path, payload) {
      const response = await fetch(this.dataset.root + path, {
        method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
        ...(payload ? { body: JSON.stringify(payload) } : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.dataset.error);
      return data;
    }
    updates(cart, options) {
      const updates = {};
      for (const option of options) {
        const lines = cart.items.filter(item => item.variant_id === option.id);
        if (lines.length) lines.forEach((line, index) => { updates[line.key] = option.selected && index === 0 ? 1 : 0; });
        else if (option.selected) updates[option.id] = 1;
      }
      return updates;
    }
    verify(cart, snapshot, quantities) {
      return Object.entries(snapshot.attributes).every(([key, value]) => (cart.attributes?.[key] || '') === value) &&
        (!quantities || snapshot.options.every(option => cart.items.filter(item => item.variant_id === option.id).reduce((sum, item) => sum + item.quantity, 0) === Number(option.selected)));
    }
    enqueue(mode) {
      clearTimeout(this.timer);
      state.queue = state.queue.catch(() => {}).then(async () => {
        const active = document.querySelector('rwm-drawer-gifts');
        if (active) await active.perform(mode);
      });
      return state.queue;
    }
    async perform(mode) {
      if (this.cardSelected() && this.field.value.length > 100) {
        this.status(this.dataset.tooLong, true);
        this.field.focus();
        state.checkout = false;
        return;
      }
      const snapshot = this.snapshot();
      const drawer = this.closest('cart-drawer');
      const modifiesProducts = mode !== 'text';
      const locked = modifiesProducts ? [...drawer.querySelectorAll('input:not(:disabled),textarea:not(:disabled),button:not(:disabled):not(.drawer__close)')] : [];
      locked.forEach(node => { node.disabled = true; });
      this.status(this.dataset.saving);
      try {
        const payload = { attributes: snapshot.attributes };
        if (modifiesProducts) {
          const cart = await this.request('cart.js');
          if (!cart.items.some(item => !snapshot.options.some(option => option.id === item.variant_id))) throw new Error(this.dataset.error);
          payload.updates = this.updates(cart, snapshot.options);
          payload.attributes['gift-wrapping'] = snapshot.options.some(option => option.kind === 'wrap' && option.selected) ? 'yes' : '';
          payload.attributes['gift-wrapping-2'] = this.cardSelected() ? 'yes' : '';
          if (mode === 'options') {
            if (typeof drawer.renderContents !== 'function') throw new Error(this.dataset.error);
            payload.sections = ['cart-drawer', 'cart-icon-bubble'];
            payload.sections_url = location.pathname;
          }
        }
        const result = await this.request('cart/update.js', payload);
        const saved = await this.request('cart.js');
        if (!this.verify(saved, snapshot, modifiesProducts)) throw new Error(this.dataset.error);
        state.saved = snapshot.revision;
        this.status(this.dataset.saved);
        if (mode === 'options') {
          if (!result.sections?.['cart-drawer'] || !result.sections?.['cart-icon-bubble']) throw new Error(this.dataset.error);
          const focusCard = this.cardSelected();
          drawer.renderContents(result);
          const active = document.querySelector('rwm-drawer-gifts');
          active?.status(this.dataset.saved);
          if (focusCard) setTimeout(() => active?.field.focus(), 250);
        } else if (mode === 'checkout') {
          location.assign(this.dataset.root + 'checkout');
        }
      } catch (error) {
        const active = document.querySelector('rwm-drawer-gifts') || this;
        active.status(this.dataset.error, true);
        state.checkout = false;
      } finally {
        locked.forEach(node => { node.disabled = false; });
      }
    }
  }
  customElements.define('rwm-drawer-gifts', RwmDrawerGifts);
  function checkout(event) {
    const component = document.querySelector('rwm-drawer-gifts');
    if (!component) return;
    const trigger = event.type === 'submit' ? event.submitter : event.target.closest('#CartDrawer-Checkout');
    if (!trigger || trigger.id !== 'CartDrawer-Checkout' || trigger.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.checkout) return;
    state.checkout = true;
    component.enqueue('checkout');
  }
  document.addEventListener('click', checkout, true);
  document.addEventListener('submit', checkout, true);
})();
